/**
 * NotFoundPage -- 404 for unknown routes (FEAT-047 TASK-023, design.md section 1).
 *
 * Full screen, no app layout: identical for anonymous users and every role. The primary
 * action goes to the role's start page; "go back" only shows when the tab has history.
 */

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { Compass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/Button';
import { RouteStatusPage } from '@/components/routing/RouteStatusPage';
import { useHomeDestination } from '@/components/routing/useHomeDestination';

const ICON_SIZE = 48;

export function NotFoundPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const home = useHomeDestination();

  const documentTitle = intl.formatMessage({ id: 'notFound.documentTitle' });
  useEffect(() => {
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle]);

  // location.key is 'default' on the first entry of the session: nothing inside the app to go back to.
  const canGoBack = location.key !== 'default' && window.history.length > 1;

  const homeLabel = intl.formatMessage(
    { id: 'notFound.action.home' },
    { destination: home.key },
  );

  return (
    <RouteStatusPage
      icon={<Compass size={ICON_SIZE} weight="regular" aria-hidden="true" />}
      eyebrow={intl.formatMessage({ id: 'notFound.eyebrow' })}
      title={intl.formatMessage({ id: 'notFound.title' })}
      description={intl.formatMessage({ id: 'notFound.description' })}
      homePath={home.path}
      homeLabel={homeLabel}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            isLoading={home.isResolving}
            onClick={() => navigate(home.path, { replace: true })}
          >
            {homeLabel}
          </Button>
          {canGoBack && (
            <Button variant="ghost" size="lg" onClick={() => navigate(-1)}>
              {intl.formatMessage({ id: 'notFound.action.back' })}
            </Button>
          )}
        </>
      }
    />
  );
}

export default NotFoundPage;
